import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import Link from 'next/link'
import { ChevronLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Eyebrow } from '@/components/generals/section-heading'

export type Crumb = {
    label: ReactNode
    href?: string
}

interface PageHeroProps {
    eyebrow: string
    title: ReactNode
    description?: ReactNode
    crumbs: Crumb[]
    image?: {
        src: string
        alt: string
        className?: string
        aspectClassName?: string
    }
    visual?: ReactNode
    children?: ReactNode
}

export const Breadcrumbs: FC<{ crumbs: Crumb[] }> = ({ crumbs }): ReactNode => {
    return (
        <nav aria-label='مسیر صفحه'>
            <ol className='flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-cream/60'>
                {crumbs.map((crumb, index) => {
                    const isLast = index === crumbs.length - 1

                    return (
                        <li key={index} className='flex items-center gap-2'>
                            {crumb.href && !isLast ? (
                                <Link href={crumb.href} className='transition-colors hover:text-gold'>
                                    {crumb.label}
                                </Link>
                            ) : (
                                <span aria-current={isLast ? 'page' : undefined} className={cn(isLast && 'text-cream/90')}>
                                    {crumb.label}
                                </span>
                            )}

                            {!isLast && <ChevronLeft aria-hidden='true' className='size-3.5 text-gold/60' />}
                        </li>
                    )
                })}
            </ol>
        </nav>
    )
}

export const PageHero: FC<PageHeroProps> = ({
    eyebrow,
    title,
    description,
    crumbs,
    image,
    visual,
    children
}): ReactNode => {
    const hasVisual = Boolean(image || visual)

    return (
        <section className='relative overflow-hidden bg-forest-950 text-cream'>
            <div aria-hidden='true' className='bg-girih pointer-events-none absolute inset-0 opacity-[0.06]' />
            <div aria-hidden='true' className='pointer-events-none absolute -top-48 -end-40 size-[36rem] rounded-full bg-gold/10 blur-3xl' />

            <div
                className={cn(
                    'relative mx-auto grid max-w-7xl gap-12 px-5 pb-16 pt-10 sm:px-8 lg:pb-20 lg:pt-14',
                    hasVisual && 'lg:grid-cols-12 lg:items-center'
                )}
            >
                <div className={cn('animate-in fade-in slide-in-from-bottom-3 duration-700', hasVisual ? 'lg:col-span-7' : 'max-w-3xl')}>
                    <Breadcrumbs crumbs={crumbs} />

                    <Eyebrow tone='dark' className='mt-10'>
                        {eyebrow}
                    </Eyebrow>

                    <h1 className='mt-4 text-[2.5rem] font-bold leading-[1.3] sm:text-5xl lg:text-[3.6rem]'>
                        {title}
                    </h1>

                    {description && (
                        <p className='mt-5 max-w-2xl text-base leading-8 text-cream/75 sm:text-lg sm:leading-9'>
                            {description}
                        </p>
                    )}

                    {children}
                </div>

                {visual && (
                    <div className='lg:col-span-5'>
                        {visual}
                    </div>
                )}

                {image && !visual && (
                    <div className='lg:col-span-5'>
                        <div
                            className={cn(
                                'relative mx-auto w-full max-w-xl overflow-hidden rounded-[2rem] border border-gold/30 bg-forest-900 shadow-[0_40px_80px_-40px_rgba(0,0,0,0.8)]',
                                image.aspectClassName ?? 'aspect-[4/3]'
                            )}
                        >
                            <Image
                                src={image.src}
                                alt={image.alt}
                                fill
                                preload
                                sizes='(min-width: 1024px) 40vw, 92vw'
                                className={cn('object-cover', image.className)}
                            />
                        </div>
                    </div>
                )}
            </div>
        </section>
    )
}
